<template>
  <div class="pos-cart">
    <!-- Cart Table -->
    <div class="pos-cart-table">
      <div class="cart-table-container">
        <table class="cart-table">
          <thead class="cart-table-header">
            <tr>
              <th class="cart-col-no">No</th>
              <th class="cart-col-item">Item</th>
              <th class="cart-col-qty">Qty</th>
              <th class="cart-col-discount">Disc %</th>
              <th class="cart-col-price">Harga</th>
              <th class="cart-col-action">Aksi</th>
            </tr>
          </thead>
          <tbody class="cart-table-body">
            <tr
              v-for="(item, k) in cart"
              :key="k"
              class="cart-table-row"
              @click="$emit('item-click', item.code)"
            >
              <td class="cart-col-no">{{ item.iterasi }}</td>
              <td class="cart-col-item">
                <div class="item-info">
                  <div class="item-code">{{ item.code }}</div>
                  <div class="item-name">{{ item.name }}</div>
                </div>
              </td>
              <td class="cart-col-qty">
                <div class="qty-controls">
                  <Button
                    type="button"
                    icon="pi pi-minus"
                    class="p-button-outlined p-button-sm qty-button"
                    @click="$emit('decrease-qty', item)"
                  />
                  <span class="qty-value">{{ item.qty }}</span>
                  <Button
                    type="button"
                    icon="pi pi-plus"
                    class="p-button-outlined p-button-sm qty-button"
                    @click="$emit('increase-qty', item)"
                  />
                </div>
              </td>
              <td class="cart-col-discount">
                <InputText
                  placeholder="%"
                  class="discount-input"
                  v-model="item.discount"
                  :maxlength="3"
                  @input="$emit('update-item-discount', item)"
                />
              </td>
              <td class="cart-col-price">
                {{ (item.qty * item.price) | currency }}
              </td>
              <td class="cart-col-action">
                <Button
                  type="button"
                  icon="pi pi-trash"
                  class="p-button-outlined p-button-danger p-button-sm delete-button"
                  @click="$emit('delete-item', item)"
                />
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty Cart State -->
        <div v-if="!hasItems" class="cart-empty">
          <div class="empty-cart-content">
            <i
              class="pi pi-shopping-cart"
              style="font-size: 3rem; color: #cbd5e1"
            ></i>
            <h3 class="empty-cart-title">Keranjang Kosong</h3>
            <p class="empty-cart-text">
              Scan barcode atau pilih produk dari katalog
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Cart Summary -->
    <div class="pos-cart-summary">
      <div class="cart-summary-content">
        <div class="summary-row">
          <span class="summary-label">Sub-Total</span>
          <span class="summary-value">{{ subTotal | currency }}</span>
        </div>
        <div class="summary-row" v-if="totalDiscount > 0">
          <span class="summary-label">Diskon</span>
          <span class="summary-value discount-value"
            >-{{ totalDiscount | currency }}</span
          >
        </div>
        <div class="summary-row" v-if="voucherAmount > 0">
          <span class="summary-label">Voucher</span>
          <span class="summary-value voucher-value"
            >-{{ voucherAmount | currency }}</span
          >
        </div>
        <div class="summary-row" v-if="discountTransactionValue > 0">
          <span class="summary-label">Diskon Cashier</span>
          <span class="summary-value discount-value"
            >-{{ discountTransactionValue | currency }}</span
          >
        </div>
        <div class="summary-row total-row">
          <span class="summary-label">Total</span>
          <span class="summary-value total-value">{{
            totalBilledAmount | currency
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'POSCart',
  props: {
    cart: {
      type: Array,
      default: () => []
    },
    isOpenShift: {
      type: Boolean,
      default: false
    },
    qtyTotal: {
      type: Number,
      default: 0
    },
    cartLength: {
      type: Number,
      default: 0
    },
    subTotal: {
      type: Number,
      default: 0
    },
    totalDiscount: {
      type: Number,
      default: 0
    },
    voucherAmount: {
      type: Number,
      default: 0
    },
    discountTransactionValue: {
      type: Number,
      default: 0
    },
    totalBilledAmount: {
      type: Number,
      default: 0
    }
  },
  computed: {
    hasItems() {
      return this.cart.length > 0
    }
  }
}
</script>

<style scoped lang="scss">
.pos-cart {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Cart Header */
.pos-cart-header {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cart-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    color: #3b82f6;
  }
}

.cart-debug {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 400;
}

.cart-stats {
  display: flex;
  gap: 1rem;
}

.cart-stat {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;

  i {
    color: #3b82f6;
  }
}

/* Cart Table */
.pos-cart-table {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Important for flex child */
}

.cart-table-container {
  flex: 1;
  overflow: auto;
  padding: 0;
  min-height: 0; /* Important for flex child */
  background: #ffffff; /* Ensure background is visible */
  display: flex;
  flex-direction: column;
}

.cart-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.cart-table-header {
  position: sticky;
  top: 0;
  background: #f8fafc;
  z-index: 10;
}

.cart-table-header th {
  padding: 0.4rem 0.35rem;
  text-align: center;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e2e8f0;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.cart-table-body {
  background: #ffffff;
  min-height: 100px; /* Ensure minimum height for table body */
}

.cart-table-body:empty {
  min-height: 200px; /* Ensure minimum height when empty */
}

.cart-table-row {
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
}

.cart-table-row td {
  padding: 0.4rem 0.35rem;
  vertical-align: middle;
}

/* Column Styles */
.cart-col-no {
  width: 40px;
  text-align: center;
  font-weight: 600;
  color: #6b7280;
}

.cart-col-item {
  width: 40%;
}

.cart-col-qty {
  width: 120px;
  text-align: center;
}

.cart-col-discount {
  width: 80px;
  text-align: center;
}

.cart-col-price {
  width: 100px;
  text-align: right;
  font-weight: 600;
  color: #059669;
  font-size: 0.95rem;
}

.cart-col-action {
  width: 50px;
  text-align: center;
}

.delete-button {
  width: 1.75rem !important;
  height: 1.75rem !important;
  padding: 0 !important;
  min-width: 1.75rem !important;

  .p-button-icon {
    font-size: 0.7rem !important;
  }
}

/* Item Info */
.item-info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.item-code {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  line-height: 1.2;
}

.item-name {
  font-size: 0.875rem;
  color: #1e293b;
  line-height: 1.3;
}

/* Quantity Controls */
.qty-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}

.qty-button {
  width: 1.75rem !important;
  height: 1.75rem !important;
  padding: 0 !important;
  min-width: 1.75rem !important;

  .p-button-icon {
    font-size: 0.7rem !important;
  }
}

.qty-value {
  font-weight: 600;
  color: #1e293b;
  min-width: 18px;
  text-align: center;
}

.discount-input {
  width: 45px;
  text-align: center;
  font-size: 0.875rem;
  padding: 0.2rem;
}

/* Empty Cart State */
.cart-empty {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  min-height: 300px; /* Ensure minimum height for empty state */
  padding: 2rem;
  background: #ffffff;
}

.empty-cart-content {
  text-align: center;
}

.empty-cart-title {
  margin: 1rem 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
}

.empty-cart-text {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
}

/* Cart Summary */
.pos-cart-summary {
  padding: 0.5rem 1.25rem;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.cart-summary-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.15rem 0;
}

.summary-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.summary-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
}

.discount-value,
.voucher-value {
  color: #dc2626;
}

/* Sub-Total Row - Increased font size */
.summary-row:first-child .summary-label,
.summary-row:first-child .summary-value {
  font-size: 1.125rem;
  font-weight: 600;
}

.total-row {
  border-top: 1px solid #e2e8f0;
  padding-top: 0.4rem;
  margin-top: 0.25rem;
}

.total-row .summary-label {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.total-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #059669;
}

/* Input Styles */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .pos-cart-header {
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
  }

  .cart-stats {
    justify-content: center;
  }

  .cart-table {
    font-size: 0.75rem;
  }

  .cart-table-header th,
  .cart-table-row td {
    padding: 0.5rem 0.25rem;
  }

  .cart-col-item {
    width: 35%;
  }

  .cart-col-qty {
    width: 100px;
  }

  .cart-col-discount {
    width: 60px;
  }

  .cart-col-price {
    width: 80px;
  }
}
</style>
